<template>
    <v-container class="pa-5" fluid>
        <OrderSendingParametersDialog
            :is-open="sendingParametersDialog"
            :order-delivery-method="order.orderDeliveryMethod"
            :delivery-methods="deliveryMethods"
            @on-close="closeSendingParametersDialog"
            @on-save="saveSendingParametersDialog"
        />

        <AdviceNoteDialog
            v-if="order && order.id"
            :is-open="adviceNoteDialog"
            :order-id="order.id"
            :advice-note="order.adviceNote"
            @on-close="closeAdviceNoteDialog"
            @on-save="handleAdviceNoteSaved"
        />

        <page-title>
            Zamówienie {{ order.productionNumber }}
            <span :class="orderStatusClass" class="text-uppercase text-body-2">
                {{ orderStatus }}
            </span>
        </page-title>
        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    :disabled="actionLoading.saveOrder"
                    @click="close"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Parametry zamówienia
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-container>
                                    <v-row v-if="isEditable">
                                        <v-col cols="12" md="3">
                                            <v-text-field
                                                :value="order.productionNumber && order.productionNumber.split('_', 1)[0]"
                                                label="Numeracja zamówienia"
                                                disabled
                                                outlined
                                                dense
                                            />
                                        </v-col>
                                        <v-col cols="12" md="9">
                                            <v-text-field
                                                v-model="order.name"
                                                label="Nazwa zamówienia"
                                                outlined
                                                :error-messages="orderNameErrors"
                                                dense
                                                :rules="validRuleOrderName"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-text-field
                                                :value="order.customer.name"
                                                label="Klient"
                                                disabled
                                                outlined
                                                dense
                                            />
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-autocomplete
                                                v-model="order.ownerId"
                                                :items="users"
                                                label="Właściciel zamówienia"
                                                item-value="id"
                                                no-data-text="Nie znaleziono"
                                                outlined
                                                dense
                                                :filter="customUsersFilter"
                                            >
                                                <template slot="selection" slot-scope="data">
                                                    <template v-if="data.item.firstName && data.item.lastName">
                                                        {{ data.item.firstName }} {{ data.item.lastName }}
                                                    </template>
                                                    <template v-else>
                                                        {{ data.item.username }}
                                                    </template>
                                                </template>
                                                <template slot="item" slot-scope="data">
                                                    <template v-if="data.item.firstName && data.item.lastName">
                                                        {{ data.item.firstName }} {{ data.item.lastName }}
                                                    </template>
                                                    <template v-else>
                                                        {{ data.item.username }}
                                                    </template>
                                                </template>
                                            </v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" md="6" sm="6">
                                            <v-text-field
                                                v-model.lazy="order.netPrice"
                                                v-money="moneySettings"
                                                label="Cena netto sprzedaży"
                                                outlined
                                                dense
                                            />
                                        </v-col>
                                        <v-col cols="12" md="6" sm="6">
                                            <v-text-field
                                                v-model.lazy="order.valuation"
                                                v-money="moneySettings"
                                                label="Koszt materiałów"
                                                outlined
                                                dense
                                            />
                                        </v-col>
                                        <v-col cols="12" sm="6" md="4">
                                            <v-datetime-picker
                                                v-model="plannedSendDate"
                                                label="Planowana data wysyłki"
                                                date-format="dd.MM.yyyy"
                                                :date-picker-props="datetimeProps.dateProps"
                                                :text-field-props="datetimeProps.textFieldProps"
                                                :time-picker-props="datetimeProps.timeProps"
                                                @input="plannedSendDateHandler"
                                            >
                                                <template slot="dateIcon">
                                                    <v-icon>mdi-calendar</v-icon>
                                                </template>
                                                <template slot="timeIcon">
                                                    <v-icon>mdi-clock-outline</v-icon>
                                                </template>
                                                <template slot="actions" slot-scope="{parent}">
                                                    <v-btn text color="error" @click.native="parent.clearHandler">
                                                        Wyczyść
                                                    </v-btn>
                                                    <v-btn text color="success" @click="parent.okHandler">
                                                        Zapisz
                                                    </v-btn>
                                                </template>
                                            </v-datetime-picker>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="4">
                                            <v-datetime-picker
                                                v-model="plannedDeliveryDate"
                                                label="Planowana data dostawy"
                                                date-format="dd.MM.yyyy"
                                                :date-picker-props="datetimeProps.dateProps"
                                                :text-field-props="datetimeProps.textFieldProps"
                                                :time-picker-props="datetimeProps.timeProps"
                                                @input="plannedDeliveryDateHandler"
                                            >
                                                <template slot="dateIcon">
                                                    <v-icon>mdi-calendar</v-icon>
                                                </template>
                                                <template slot="timeIcon">
                                                    <v-icon>mdi-clock-outline</v-icon>
                                                </template>
                                                <template slot="actions" slot-scope="{parent}">
                                                    <v-btn text color="error" @click.native="parent.clearHandler">
                                                        Wyczyść
                                                    </v-btn>
                                                    <v-btn text color="success" @click="parent.okHandler">
                                                        Zapisz
                                                    </v-btn>
                                                </template>
                                            </v-datetime-picker>
                                        </v-col>
                                        <v-col cols="12" md="4" sm="6">
                                            <v-menu
                                                v-model="receiveMaterialDatepicker"
                                                :close-on-content-click="false"
                                                transition="scale-transition"
                                                bottom
                                                nudge-bottom="40"
                                                nudge-right="34"
                                                min-width="290px"
                                            >
                                                <template #activator="{on}">
                                                    <v-text-field
                                                        :value="formatDate(order.receiveMaterialDate, false, false)"
                                                        label="Data dostawy surowca"
                                                        prepend-icon="mdi-calendar"
                                                        readonly
                                                        outlined
                                                        dense
                                                        :clearable="!!order.receiveMaterialDate"
                                                        v-on="on"
                                                        @click:clear="clearReceiveMaterialDate"
                                                    />
                                                </template>
                                                <v-date-picker
                                                    :value="receiveMaterialDate"
                                                    no-title
                                                    scrollable
                                                    first-day-of-week="1"
                                                    @input="receiveMaterialDateHandler"
                                                />
                                            </v-menu>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="4">
                                            <v-menu
                                                v-model="receiveFileDatepicker"
                                                :close-on-content-click="false"
                                                transition="scale-transition"
                                                bottom
                                                nudge-bottom="40"
                                                nudge-right="34"
                                                min-width="290px"
                                            >
                                                <template #activator="{on}">
                                                    <v-text-field
                                                        :value="formatDate(order.receiveFileDate, false, false)"
                                                        label="Data dostarczenia plików"
                                                        prepend-icon="mdi-calendar"
                                                        readonly
                                                        outlined
                                                        dense
                                                        :clearable="!!order.receiveFileDate"
                                                        v-on="on"
                                                        @click:clear="clearReceiveFileDate"
                                                    />
                                                </template>
                                                <v-date-picker
                                                    :value="receiveFileDate"
                                                    no-title
                                                    scrollable
                                                    first-day-of-week="1"
                                                    @input="receiveFileDateHandler"
                                                />
                                            </v-menu>
                                        </v-col>
                                        <v-col cols="12" md="4" sm="6">
                                            <v-menu
                                                v-model="acceptFileDatepicker"
                                                :close-on-content-click="false"
                                                transition="scale-transition"
                                                bottom
                                                nudge-bottom="40"
                                                nudge-right="34"
                                                min-width="290px"
                                            >
                                                <template #activator="{on}">
                                                    <v-text-field
                                                        :value="formatDate(order.acceptFileDate, false, false)"
                                                        label="Data akceptacji plików"
                                                        prepend-icon="mdi-calendar"
                                                        readonly
                                                        outlined
                                                        dense
                                                        :clearable="!!order.acceptFileDate"
                                                        v-on="on"
                                                        @click:clear="clearAcceptFileDate"
                                                    />
                                                </template>
                                                <v-date-picker
                                                    :value="acceptFileDate"
                                                    no-title
                                                    scrollable
                                                    first-day-of-week="1"
                                                    @input="acceptFileDateHandler"
                                                />
                                            </v-menu>
                                        </v-col>
                                        <v-col cols="12" md="4" sm="6">
                                            <v-select
                                                v-model="order.orderGraphicFileDeliveryMethodId"
                                                :items="orderGraphicFileDeliveryMethods"
                                                item-text="name"
                                                item-value="id"
                                                label="Metoda dostarczenia plików graficznych"
                                                :error-messages="orderGraphicFileDeliveryMethodErrors"
                                                outlined
                                                dense
                                            />
                                        </v-col>
                                        <v-col v-if="showTransportSection" cols="12" md="4">
                                            <v-text-field
                                                :value="getDeliveryMethodName(order.orderDeliveryMethod.methodId)"
                                                label="Transport"
                                                prepend-icon="mdi-truck"
                                                outlined
                                                dense
                                                readonly
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col v-if="showTransportSection" cols="12" md="2">
                                            <v-text-field
                                                :value="order.orderDeliveryMethod.distance"
                                                label="Dystans"
                                                suffix="km"
                                                outlined
                                                dense
                                                readonly
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col v-if="showTransportSection" cols="12" md="2">
                                            <v-text-field
                                                :value="order.orderDeliveryMethod.cost"
                                                label="Koszt"
                                                suffix="zł"
                                                outlined
                                                dense
                                                readonly
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col v-if="showTransportSection" cols="auto">
                                            <div class="d-inline-flex">
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            v-on="on"
                                                            @click="showSendingParametersDialog()"
                                                        >
                                                            <v-icon color="blue">
                                                                mdi-pencil
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Edytuj</span>
                                                </v-tooltip>
                                            </div>
                                        </v-col>
                                        <v-col v-if="isEditable" cols="auto">
                                            <v-btn
                                                color="secondary"
                                                data-sentry-label="Awizacja od klienta"
                                                @click="showAdviceNoteDialog"
                                            >
                                                Awizacja od klienta
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-textarea
                                                v-model="order.description"
                                                label="Uwagi do zamówienia"
                                                hide-details
                                                outlined
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row v-else>
                                        <v-col cols="12" md="6">
                                            <table class="table table--simple">
                                                <tbody>
                                                    <tr>
                                                        <th>Typ zamówienia:</th>
                                                        <td>{{ order.productionNumber && order.productionNumber.split('_', 1)[0] }}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Nazwa zamówienia:</th>
                                                        <td>{{ parseEmptyValue(order.name) }}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Klient:</th>
                                                        <td>{{ order.customer.name }}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Właściciel:</th>
                                                        <td>{{ formatOwnerName(order.owner) }}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Cena netto sprzedaży:</th>
                                                        <td>{{ formatPrice(order.netPrice) }} zł</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Koszt materiałów:</th>
                                                        <td>{{ formatPrice(order.valuation) }} zł</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <table class="table table--simple">
                                                <tbody>
                                                    <tr>
                                                        <th>Data zamówienia:</th>
                                                        <td>{{ formatDate(order.createdAt) }}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Planowana data wysyłki:</th>
                                                        <td>{{ formatDate(order.plannedSendDate) }}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Planowana data dostawy:</th>
                                                        <td>{{ formatDate(order.plannedDeliveryDate) }}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Data akceptacji surowca:</th>
                                                        <td>{{ formatDate(order.receiveMaterialDate, false) }}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Data dostarczenia plików:</th>
                                                        <td>{{ formatDate(order.receiveFileDate, false) }}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Data akceptacji plików:</th>
                                                        <td>{{ formatDate(order.acceptFileDate, false) }}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Metoda dostarczenia plików graficznych:</th>
                                                        <td>
                                                            <template v-if="order.orderGraphicFileDeliveryMethod">
                                                                {{ parseEmptyValue(order.orderGraphicFileDeliveryMethod.name) }}
                                                            </template>
                                                            <template v-else>
                                                                -
                                                            </template>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </v-col>
                                        <v-col v-if="order.description" cols="12">
                                            <table class="table table--simple table--align-left">
                                                <tbody>
                                                    <tr>
                                                        <th>Uwagi do zamówienia:</th>
                                                        <td width="100%">
                                                            {{ order.description }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container>
                    <v-row align="center">
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Produkty
                            </h2>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-card-text>
                                    <v-data-table
                                        :headers="headers"
                                        :items="order.orderProducts"
                                        disable-pagination
                                        hide-default-footer
                                        :mobile-breakpoint="0"
                                        item-key="index"
                                    >
                                        <template #item.productName="{item}">
                                            {{ item.name }}
                                        </template>
                                        <template #item.productionFile="{item}">
                                            {{ productionFileName(item) }}
                                        </template>
                                        <template #item.extraAmount="{item}">
                                            {{ item.extraAmount }} szt.
                                        </template>
                                        <template #item.nettoAmount="{item}">
                                            {{ item.nettoAmount }} szt.
                                        </template>
                                        <template #item.numberOfVersions="{item}">
                                            {{ item.numberOfVersions }}
                                        </template>
                                    </v-data-table>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Plan dostaw
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-card-text>
                                    <v-list v-if="hasAttachments" dense>
                                        <v-list-item
                                            v-for="(item, index) in uploadedFiles"
                                            :key="index"
                                            @click="openInNewTab(item)"
                                        >
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    {{ fileName(item) }}
                                                </v-list-item-title>
                                            </v-list-item-content>

                                            <v-list-item-icon>
                                                <v-icon color="primary">
                                                    mdi-download
                                                </v-icon>
                                            </v-list-item-icon>
                                        </v-list-item>
                                    </v-list>
                                    <v-alert
                                        v-else
                                        type="warning"
                                        text
                                        border="left"
                                    >
                                        Nie załączono planu dostaw
                                    </v-alert>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Komentarze do zamówienia
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-list v-if="hasComments" two-line>
                                    <template v-for="(comment, index) in order.comments">
                                        <v-list-item
                                            :key="comment.createdAt"
                                        >
                                            <v-list-item-avatar>
                                                <v-avatar
                                                    size="40px"
                                                    color="blue-grey"
                                                >
                                                    <span class="white--text text-uppercase">
                                                        {{ parseUserInitials(comment.username) }}
                                                    </span>
                                                </v-avatar>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    {{ comment.comment }}
                                                </v-list-item-title>
                                                <v-list-item-subtitle>
                                                    {{ comment.username }} - {{ formatDate(comment.createdAt) }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider :key="index" />
                                    </template>
                                </v-list>
                                <v-card-text v-else>
                                    <v-alert
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Brak komentarzy do zamówienia.
                                    </v-alert>
                                </v-card-text>

                                <v-card-text>
                                    <v-textarea
                                        v-model="newComment"
                                        label="Dodaj komentarz"
                                        :disabled="actionLoading.addComment"
                                        auto-grow
                                        clearable
                                        hide-details
                                    />
                                    <v-row justify="end">
                                        <v-col>
                                            <v-btn
                                                color="primary"
                                                data-sentry-label="Wyślij komentarz"
                                                :disabled="newComment === null || newComment.length <= 0"
                                                :loading="actionLoading.addComment"
                                                @click="addComment"
                                            >
                                                Wyślij
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <v-btn
                                v-if="isEditable"
                                color="success"
                                data-sentry-label="Zapisz"
                                :loading="actionLoading.saveOrder"
                                @click="saveOrder"
                            >
                                Zapisz
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'
import store from '@/./store'

import PageTitle from '@/components/common/PageTitle'
import OrderSendingParametersDialog from './OrderSendingParametersDialog'
import AdviceNoteDialog from '@/components/common/AdviceNoteDialog'

import dateFormatter from '@/mixins/dateFormatter'
import priceFormatter from '@/mixins/priceFormatter'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse'
import attachment from '@/mixins/attachment'
import customFilters from '@/mixins/customFilters'

import { errorHandler } from '@/utils/errorHandler'
import { required, helpers } from 'vuelidate/lib/validators'
import { VMoney } from 'v-money'
import { validRuleIllegalCharacters, validRuleNameLength } from '@/utils/customValidators'

const TRADE_DEPARTMENT_ID = 6

export default {
    name: 'Order',
    components: {
        PageTitle,
        OrderSendingParametersDialog,
        AdviceNoteDialog
    },
    directives: { money: VMoney },
    mixins: [ dateFormatter, priceFormatter, tooltipDelay, parse, customFilters, attachment ],
    beforeRouteEnter(to, from, next) {
        Promise.all([
            store.dispatch('orders/getOrderGraphicFileDeliveryMethods'),
            store.dispatch('orders/getOrder', to.params.id),
            store.dispatch('administration/getUsers', {
                query: `?productionDepartment_id=${TRADE_DEPARTMENT_ID}&isActive=True`
            }),
            store.dispatch('shipments/shipmentsOrders/getDeliveryMethods')
        ])
            .then(responses => {
                next(vm => {
                    vm.orderGraphicFileDeliveryMethods = responses[0].data.results
                    vm.order = responses[1].data
                    vm.plannedSendDate = vm.order.plannedSendDate ? new Date(vm.order.plannedSendDate) : null
                    vm.plannedDeliveryDate = vm.order.plannedDeliveryDate ? new Date(vm.order.plannedDeliveryDate) : null
                    vm.receiveFileDate = vm.order.receiveFileDate ? new Date(vm.order.receiveFileDate) : null
                    vm.createUploadedUrls(vm.order.orderAttachment)
                    vm.users = responses[2].data.results
                    vm.deliveryMethods = responses[3].data.results
                    if (!vm.order.orderDeliveryMethod) {
                        vm.order.orderDeliveryMethod = {
                            methodId: null,
                            cost: null,
                            distance: null
                        }
                    }
                })
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    data() {
        return {
            orderGraphicFileDeliveryMethods: [],
            deliveryMethods: [],
            order: {
                name: null,
                status: {
                    id: null
                },
                productionNumber: null,
                customer: {
                    name: null
                },
                netPrice: null,
                plannedSendDate: null,
                plannedDeliveryDate: null,
                receiveMaterialDate: null,
                receiveFileDate: null,
                acceptFileDate: null,
                orderAttachment: [],
                orderGraphicFileDeliveryMethod: {},
                orderGraphicFileDeliveryMethodId: null,
                description: null,
                ownerId: null,
                owner: {
                    firstName: null,
                    lastName: null
                },
                orderDeliveryMethod: {
                    methodId: null,
                    cost: null,
                    distance: null
                }
            },
            plannedSendDate: null,
            plannedDeliveryDate: null,
            receiveMaterialDate: null,
            receiveFileDate: null,
            acceptFileDate: null,
            receiveMaterialDatepicker: false,
            receiveFileDatepicker: false,
            acceptFileDatepicker: false,
            datetimeProps: {
                textFieldProps: {
                    outlined: true,
                    dense: true,
                    prependIcon: 'mdi-calendar'
                },
                dateProps: {
                    firstDayOfWeek: 1
                },
                timeProps: {
                    format: '24hr',
                    scrollable: true
                }
            },
            newComment: '',
            actionLoading: {
                addComment: false,
                saveOrder: false
            },
            sendingParametersDialog: false,
            adviceNoteDialog: false,
            headers: [
                {
                    text: 'Nazwa produktu',
                    value: 'productName',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Pliki produkcyjne',
                    value: 'productionFile',
                    align: 'left',
                    sortable: false
                },
                {
                    text: 'Nakład',
                    value: 'nettoAmount',
                    align: 'center',
                    sortable: false,
                    width: 150
                },
                {
                    text: 'Naddatek',
                    value: 'extraAmount',
                    align: 'center',
                    sortable: false,
                    width: 150
                },
                {
                    text: 'Ilość wzorów',
                    value: 'numberOfVersions',
                    align: 'center',
                    sortable: false,
                    width: 150
                }
            ],
            users: [],
            validRuleOrderName: [
                value => validRuleNameLength(value) || 'Nazwa nie może być dłuższa niż 80 znaków',
                value => !validRuleIllegalCharacters(value) || 'Niedozwolone znaki –  /:\'"?*<>|()[]%$#@{}~^&'
            ],
        }
    },
    validations() {
        const orderGraphicFileDeliveryMethodHelper = () => {
            return helpers.withParams({ type: 'orderGraphicFileDeliveryMethodHelper' }, (value) => {
                return value === 4 ? this.hasAllProductsProductionFiles : true
            })
        }

        return {
            order: {
                name: {
                    required,
                    isNameValid: value => 
                        validRuleNameLength(value) &&
                        !validRuleIllegalCharacters(value),
                },
                orderGraphicFileDeliveryMethodId: {
                    isValid: orderGraphicFileDeliveryMethodHelper()
                }
            }
        }
    },
    computed: {
        isEditable() {
            return this.order.status.id === 10 // new order
        },
        hasAttachments() {
            return this.order.orderAttachment.length > 0
        },
        hasComments() {
            return this.order.comments ? this.order.comments.length > 0 : false
        },
        hasAllProductsProductionFiles() {
            return this.order.orderProducts.every(product => {
                return product.orderProductAttachment.length > 0
            })
        },
        orderStatus() {
            let orderStatus = null

            this.order.overdue ? orderStatus = 'opóźnione' : orderStatus = this.order.status.name

            return orderStatus
        },
        orderStatusClass() {
            let orderStatusClass = ''

            if (this.order.overdue) {
                orderStatusClass = 'warning--text'

                return orderStatusClass
            }

            switch (this.order.status.id) {
                case 10:
                case 15:
                    orderStatusClass = 'info--text'
                    break
                case 50:
                    orderStatusClass = 'success--text'
                    break
                default:
                    break
            }

            return orderStatusClass
        },
        userName() {
            return this.$store.state.user.userName
        },
        // Walidacje
        orderNameErrors() {
            const errors = []

            if (!this.$v.order.name.$dirty) return errors
            !this.$v.order.name.required && errors.push('Proszę podać nazwę zamówienia.')

            !validRuleNameLength(this.$v.order.name.$model) && errors.push('Nazwa nie może być dłuższa niż 80 znaków')
            validRuleIllegalCharacters(this.$v.order.name.$model) && errors.push('Niedozwolone znaki –  /:\'"?*<>|()[]%$#@{}~^&')

            return errors
        },
        orderGraphicFileDeliveryMethodErrors() {
            const errors = []

            if (!this.$v.order.orderGraphicFileDeliveryMethodId.$dirty) return errors
            !this.$v.order.orderGraphicFileDeliveryMethodId.isValid && errors.push('Metoda wymaga plików produkcyjnych w produktach.')

            return errors
        },
        showTransportSection() {
            return !this.order.campaignId && !this.order.linkedToCampaign
        }
    },
    methods: {
        productionFileName(item) {
            if (item.orderProductAttachment.length > 0) {
                const first_attachment = item.orderProductAttachment[0]

                return first_attachment.file ? first_attachment.file : first_attachment.urlToExternalFile
            }

            return '-'
        },
        formatOwnerName(owner) {
            return owner ? `${owner.firstName} ${owner.lastName}` : '-'
        },
        getOrder() {
            this.$store.dispatch('orders/getOrder', this.order.id)
                .then(response => {
                    this.order = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        plannedSendDateHandler(date) {
            this.plannedSendDate = date
            date ? this.order.plannedSendDate = date.toISOString() : this.order.plannedSendDate = null
        },
        plannedDeliveryDateHandler(date) {
            this.plannedDeliveryDate = date
            date ? this.order.plannedDeliveryDate = date.toISOString() : this.order.plannedDeliveryDate = null
        },
        receiveMaterialDateHandler(date) {
            this.receiveMaterialDate = date
            this.order.receiveMaterialDate = `${date}T00:00:00Z`
            this.receiveMaterialDatepicker = false
        },
        receiveFileDateHandler(date) {
            this.receiveFileDate = date
            this.order.receiveFileDate = `${date}T00:00:00Z`
            this.receiveFileDatepicker = false
        },
        acceptFileDateHandler(date) {
            this.acceptFileDate = date
            this.order.acceptFileDate = `${date}T00:00:00Z`
            this.acceptFileDatepicker = false
        },
        clearReceiveMaterialDate() {
            this.receiveMaterialDate = null,
            this.order.receiveMaterialDate = null
        },
        clearReceiveFileDate() {
            this.receiveFileDate = null,
            this.order.receiveFileDate = null
        },
        clearAcceptFileDate() {
            this.acceptFileDate = null,
            this.order.acceptFileDate = null
        },
        addComment() {
            this.actionLoading.addComment = true

            this.$store.dispatch('orders/addComment', {
                comment: this.newComment,
                username: this.userName,
                orderId: this.order.id
            })
                .then(response => {
                    this.order.comments.push(response.data)
                    this.newComment = ''
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.addComment = false
                })
        },
        saveOrder() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            this.actionLoading.saveOrder = true

            this.$store.dispatch('orders/updateOrder', {
                id: this.order.id,
                name: this.order.name,
                netPrice: this.unformatPrice(this.order.netPrice),
                valuation: this.unformatPrice(this.order.valuation),
                plannedSendDate: this.order.plannedSendDate,
                plannedDeliveryDate: this.order.plannedDeliveryDate,
                receiveMaterialDate: this.order.receiveMaterialDate,
                receiveFileDate: this.order.receiveFileDate,
                acceptFileDate: this.order.acceptFileDate,
                orderGraphicFileDeliveryMethodId: this.order.orderGraphicFileDeliveryMethodId,
                description: this.order.description,
                ownerId: this.order.ownerId,
                orderDeliveryMethod: this.order.orderDeliveryMethod
            })
                .then(() => {
                    router.push({ name: 'ordersInternalOrders' })
                        .catch(error => {
                            errorHandler(error)
                        })
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.saveOrder = false
                })
        },
        fileName(file) {
            const words = file.split('/')

            return words[words.length - 1]
        },
        close() {
            router.push({ name: 'ordersInternalOrders' })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showSendingParametersDialog() {
            this.sendingParametersDialog = true
        },
        closeSendingParametersDialog() {
            this.sendingParametersDialog = false
        },
        saveSendingParametersDialog(deliveryData) {
            this.order.orderDeliveryMethod = JSON.parse(JSON.stringify(deliveryData))
            this.closeSendingParametersDialog()
        },
        getDeliveryMethodName(id) {
            const method = this.deliveryMethods.find(item => item.id === id)

            return method ? method.name : ''
        },
        showAdviceNoteDialog() {
            this.adviceNoteDialog = true
        },
        closeAdviceNoteDialog() {
            this.adviceNoteDialog = false
        },
        handleAdviceNoteSaved(adviceNoteData) {
            if (adviceNoteData) {
                this.$set(this.order, 'adviceNote', adviceNoteData)
            }
        }
    }
}
</script>
